import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/app/AuthProvider';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { WorkPhotoCapture } from '@/components/work/WorkPhotoCapture';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { useCreateScrapLog } from '@/features/scrap/hooks';
import { SCRAP_TAGS, scrapTagLabel, slugifyScrapTag } from '@/features/scrap/tags';
import type { Item } from '@/features/inventory/hooks';
import { describeWorkError } from '@/features/workMode/api';
import { useBomComponents, uploadCapture } from '@/features/workMode/hooks';
import { compressImage } from '@/features/workMode/images';
import {
  DEFECT_SEVERITIES,
  type ScrappedPart,
  type WorkDefectSeverity,
} from '@/features/workMode/types';
import type { WorkLookups } from '@/features/workMode/useLookups';
import { cn } from '@/lib/utils';

const NO_STATION = '__none__';
const MAX_MINUTES = 7 * 24 * 60;

interface AddScrapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lookups: WorkLookups;
}

/**
 * Office form for one scrapped unit. Persists through `scrap_log_create`
 * so the row, its cost, and the scrap-rate counters are written on the server.
 */
export function AddScrapDialog({ open, onOpenChange, lookups }: AddScrapDialogProps) {
  const { profile } = useAuth();
  const create = useCreateScrapLog();
  const [busy, setBusy] = useState(false);
  const [item, setItem] = useState<Item | null>(null);
  const [userId, setUserId] = useState('');
  const [workstationId, setWorkstationId] = useState(NO_STATION);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<WorkDefectSeverity>('major');
  const [tags, setTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [minutes, setMinutes] = useState('');
  const [progress, setProgress] = useState('');
  const [needsRework, setNeedsRework] = useState(false);
  const [reworkNotes, setReworkNotes] = useState('');
  const [parts, setParts] = useState<ScrappedPart[]>([]);
  const [extraName, setExtraName] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);

  const { data: components = [] } = useBomComponents(open ? (item?.id ?? null) : null);
  const people = useMemo(() => lookups.peopleList.filter((p) => p.isActive), [lookups.peopleList]);
  const stations = useMemo(
    () => lookups.stationList.filter((s) => s.is_active),
    [lookups.stationList],
  );
  const knownIds = useMemo(() => new Set(parts.map((p) => p.item_id).filter(Boolean)), [parts]);

  useEffect(() => {
    if (!open) return;
    setItem(null);
    setUserId(profile?.id ?? '');
    setWorkstationId(NO_STATION);
    setDescription('');
    setSeverity('major');
    setTags([]);
    setCustomTag('');
    setMinutes('');
    setProgress('');
    setNeedsRework(false);
    setReworkNotes('');
    setParts([]);
    setExtraName('');
    setPhotos([]);
    setBusy(false);
  }, [open, profile?.id]);

  const toggleTag = (value: string) => {
    setTags((cur) =>
      cur.includes(value)
        ? cur.filter((t) => t !== value)
        : cur.length >= 8
          ? cur
          : [...cur, value],
    );
  };

  const addCustomTag = () => {
    const slug = slugifyScrapTag(customTag);
    if (!slug) return;
    setTags((cur) => (cur.includes(slug) || cur.length >= 8 ? cur : [...cur, slug]));
    setCustomTag('');
  };

  const setQty = (index: number, qty: number) => {
    setParts((cur) => cur.map((p, i) => (i === index ? { ...p, qty: Math.max(0, qty) } : p)));
  };

  const addBomPart = (itemId: string) => {
    const c = components.find((row) => row.item_id === itemId);
    if (!c || knownIds.has(itemId)) return;
    setParts((cur) => [...cur, { item_id: c.item_id, sku: c.sku, name: c.name, qty: 1 }]);
  };

  const addExtraPart = () => {
    const name = extraName.trim();
    if (!name) return;
    setParts((cur) => [...cur, { item_id: null, sku: '', name, qty: 1 }]);
    setExtraName('');
  };

  const submit = async () => {
    if (!item) {
      toast.error('Pick the part that was scrapped.');
      return;
    }
    if (!userId) {
      toast.error('Pick who scrapped it.');
      return;
    }
    if (description.trim().length === 0) {
      toast.error('The scrap log needs a description.');
      return;
    }
    if (tags.length === 0) {
      toast.error('Tag the type of scrap — pick at least one.');
      return;
    }
    const committedSeconds = parseMinutes(minutes);
    if (committedSeconds === 'invalid') {
      toast.error('Time already spent has to be a whole number of minutes, up to a week.');
      return;
    }
    const progressPct = parseProgress(progress);
    if (progressPct === 'invalid') {
      toast.error('How far along has to be a percent from 0 to 100.');
      return;
    }
    if (!profile?.organization_id) {
      toast.error('You are not allowed to do that.');
      return;
    }

    setBusy(true);
    try {
      const photoPaths: string[] = [];
      for (let i = 0; i < photos.length; i += 1) {
        const file = photos[i];
        const blob = await compressImage(file);
        photoPaths.push(
          await uploadCapture(
            profile.organization_id,
            'manual',
            blob,
            file.name.replace(/\.[^.]+$/, '') + `-${i}.jpg`,
          ),
        );
      }
      await create.mutateAsync({
        description: description.trim(),
        scrapTags: tags,
        itemId: item.id,
        userId,
        workstationId: workstationId === NO_STATION ? null : workstationId,
        severity,
        needsRework,
        reworkNotes: needsRework ? reworkNotes.trim() || null : null,
        scrappedParts: parts.filter((p) => p.name.trim() && p.qty > 0),
        photoPaths,
        committedSeconds,
        progressPct,
      });
      toast.success('Scrap logged.');
      onOpenChange(false);
    } catch (e) {
      toast.error(describeWorkError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-fresh-salmon-dark" />
            Log scrap
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {item ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-secondary/30 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs text-muted-foreground">{item.sku}</div>
                <div className="truncate font-medium">{item.name}</div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setItem(null);
                  setParts([]);
                }}
              >
                <X className="h-4 w-4" />
                Change
              </Button>
            </div>
          ) : (
            <ItemPicker
              label="Part *"
              value={null}
              onChange={(picked) => {
                setItem(picked);
                setParts([]);
              }}
              placeholder="Search parts by SKU or name…"
              filter={(it) => it.is_active}
              autoFocus
            />
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="scrap-who">
                Who <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Select value={userId || undefined} onValueChange={setUserId} disabled={busy}>
                <SelectTrigger id="scrap-who">
                  <SelectValue placeholder="Pick a person" />
                </SelectTrigger>
                <SelectContent>
                  {people.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="scrap-station">Workstation</Label>
              <Select value={workstationId} onValueChange={setWorkstationId} disabled={busy}>
                <SelectTrigger id="scrap-station">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_STATION}>None</SelectItem>
                  {stations.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scrap-add-description">
              What went wrong <span className="text-fresh-salmon-dark">*</span>
            </Label>
            <Textarea
              id="scrap-add-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              disabled={busy}
              placeholder="e.g. Cracked boss on the left mount after torquing the M6 bolt."
            />
          </div>

          <div className="space-y-2">
            <Label>
              Type of scrap <span className="text-fresh-salmon-dark">*</span>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {SCRAP_TAGS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  disabled={busy}
                  onClick={() => toggleTag(t.value)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs',
                    tags.includes(t.value)
                      ? 'border-info bg-info-soft/30 text-info'
                      : 'border-border text-muted-foreground hover:bg-secondary',
                  )}
                >
                  {t.label}
                </button>
              ))}
              {tags
                .filter((t) => !SCRAP_TAGS.some((p) => p.value === t))
                .map((t) => (
                  <button
                    key={t}
                    type="button"
                    disabled={busy}
                    onClick={() => toggleTag(t)}
                    className="rounded-full border border-info bg-info-soft/30 px-2.5 py-1 text-xs text-info"
                  >
                    {scrapTagLabel(t)} · remove
                  </button>
                ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={customTag}
                onChange={(e) => setCustomTag(e.target.value)}
                placeholder="Add a type"
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTag();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={busy || !slugifyScrapTag(customTag)}
                onClick={addCustomTag}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>How bad</Label>
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as WorkDefectSeverity)}
                disabled={busy}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEFECT_SEVERITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="scrap-minutes">Time already spent</Label>
              <Input
                id="scrap-minutes"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value.replace(/[^\d]/g, ''))}
                inputMode="numeric"
                placeholder="Minutes"
                disabled={busy}
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="scrap-progress">How far along</Label>
              <Input
                id="scrap-progress"
                value={progress}
                onChange={(e) => setProgress(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
                inputMode="numeric"
                placeholder="%"
                disabled={busy}
                className="tabular-nums"
              />
            </div>
          </div>

          <div className="space-y-2 rounded-xl border border-border p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground">Needs rework</p>
              <Switch
                checked={needsRework}
                onCheckedChange={setNeedsRework}
                disabled={busy}
                aria-label="Needs rework"
              />
            </div>
            {needsRework && (
              <Textarea
                value={reworkNotes}
                onChange={(e) => setReworkNotes(e.target.value)}
                rows={2}
                disabled={busy}
                placeholder="What should be reworked?"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Components thrown out</Label>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {parts.length === 0 && (
                <li className="px-3 py-2 text-sm text-muted-foreground">None recorded.</li>
              )}
              {parts.map((p, i) => (
                <li
                  key={`${p.item_id ?? p.name}-${i}`}
                  className="flex items-center gap-2 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                    {p.sku && <p className="truncate text-xs text-muted-foreground">{p.sku}</p>}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    disabled={busy || p.qty === 0}
                    onClick={() => setQty(i, p.qty - 1)}
                    aria-label={`Fewer ${p.name}`}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    value={String(p.qty)}
                    onChange={(e) => setQty(i, Number(e.target.value.replace(/[^0-9.]/g, '')) || 0)}
                    className="h-9 w-16 text-center tabular-nums"
                    inputMode="decimal"
                    disabled={busy}
                    aria-label={`Quantity of ${p.name}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    disabled={busy}
                    onClick={() => setQty(i, p.qty + 1)}
                    aria-label={`More ${p.name}`}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
            {components.some((c) => !knownIds.has(c.item_id)) && (
              <div className="flex flex-wrap gap-1.5">
                {components
                  .filter((c) => !knownIds.has(c.item_id))
                  .map((c) => (
                    <Button
                      key={c.item_id}
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => addBomPart(c.item_id)}
                    >
                      Add {c.name}
                    </Button>
                  ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={extraName}
                onChange={(e) => setExtraName(e.target.value)}
                placeholder="Other part (name or SKU)"
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && extraName.trim()) {
                    e.preventDefault();
                    addExtraPart();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={busy || !extraName.trim()}
                onClick={addExtraPart}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Photos</Label>
            <WorkPhotoCapture files={photos} onChange={setPhotos} disabled={busy} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Saving…' : 'Log scrap'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseMinutes(raw: string): number | null | 'invalid' {
  const text = raw.trim();
  if (text === '') return null;
  if (!/^\d+$/.test(text)) return 'invalid';
  const minutes = Number(text);
  if (minutes > MAX_MINUTES) return 'invalid';
  return minutes * 60;
}

function parseProgress(raw: string): number | null | 'invalid' {
  const text = raw.trim();
  if (text === '') return null;
  if (!/^\d+$/.test(text)) return 'invalid';
  const value = Number(text);
  if (value > 100) return 'invalid';
  return value;
}
